import React, { useState, useEffect, useRef, memo } from "react";
import {
  AreaSeries,
  CandlestickSeries,
  createChart,
  createSeriesMarkers,
  createTextWatermark,
  HistogramSeries,
  ISeriesApi,
  Time,
} from "lightweight-charts-v5";
import { useDarkTheme } from "@/contexts/DarkTheme";
import { fetchColor, formatEpochDateTime } from "@/helpers/helperFunctions";
import { AccentColor } from "@/helpers/types";
import { addGrid, addIndicatorToChart } from "./Indicators";
import { TickMarkType } from "lightweight-charts";
import { TrendLine } from "./lightweight-charts/plugins/trend-line/trend-line";
import { isEmpty } from "lodash";
import { usePostCommonIndicators } from "@/api/strategyApi";

interface LightWeightChartProps {
  gridData?: any;
  graphData: any[];
  volumeData: any[];
  logsData?: any[];
  graphDataOtherDetails: any;
  canvasData: string;
  enableIndicator: boolean;
  currentPrice?: number;
  invertedChart?: boolean;
  monotone?: boolean;
}

const LightWeightChart: React.FC<LightWeightChartProps> = ({
  gridData,
  graphData,
  volumeData,
  logsData,
  graphDataOtherDetails,
  canvasData,
  enableIndicator,
  invertedChart,
  currentPrice,
  monotone,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const candleStickSeriesRef = useRef<any>(null);

  const chartRef = useRef<any>(null);
  const indicatorSeriesRef = useRef<Record<string, ISeriesApi<"Line">[]>>({});
  const { mutateAsync: postCommonIndicators } = usePostCommonIndicators();

  const { darkTheme } = useDarkTheme();
  const [loading, setLoading] = useState(false);
  const indicatorsLoaded = useRef<boolean>(false);
  const [chartLoaded, setChartLoaded] = useState<boolean>(false);
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, [darkTheme]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const colorMap = fetchColor({
      background: "transparent",
      foreground: "hsl(var(--foreground))",
      muted: "hsl(var(--muted))",
      mutedForeground: "hsl(var(--muted-foreground))",
      primary: "hsl(var(--marketplace-blue))",
      transparent: "#ffffffff",
      accent: "hsl(var(--accent));",
    });

    const chart = createChart(chartContainerRef.current, {
      crosshair: { mode: 1 },
      layout: {
        background: { color: colorMap.background },
        textColor: colorMap.foreground,
        panes: {
          separatorColor: "#316acd",
          separatorHoverColor: "#316acd",
          // setting this to false will disable the resize of the panes by the user
          enableResize: true,
        },
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      localization: {
        timeFormatter: (time: number) => {
          return formatEpochDateTime(time, "DD-MMM-YY HH:mm", true);
        },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: true,
        tickMarkFormatter: (
          time: string,
          tickMarkType: TickMarkType,
          locale: string
        ) => {
          if (tickMarkType === TickMarkType.Time) {
            return formatEpochDateTime(time, "HH:mm", true);
          } else if (tickMarkType === TickMarkType.DayOfMonth) {
            return formatEpochDateTime(time, "DD", true); // Converts Unix timestamp (in seconds) to local date
          } else if (tickMarkType === TickMarkType.Month) {
            return formatEpochDateTime(time, "MMM", true);
          }
          return null;
        },
      },
    });

    const lineSeries = chart.addSeries(CandlestickSeries, {
      wickUpColor: monotone ? AccentColor.DARKGREY : AccentColor.GREEN,
      upColor: monotone ? AccentColor.DARKGREY : AccentColor.GREEN,
      wickDownColor: monotone ? AccentColor.DARKGREY : AccentColor.RED,
      downColor: monotone ? AccentColor.DARKGREY : AccentColor.RED,
      borderVisible: false,
    });

    lineSeries.setData([...graphData]);
    candleStickSeriesRef.current = lineSeries;

    const volumeSeries = chart.addSeries(HistogramSeries, {
      color: "#26a69a",
      priceFormat: { type: "volume" },
      priceScaleId: "",
    });

    volumeSeries.priceScale().applyOptions({
      scaleMargins: {
        top: 0.9,
        bottom: 0,
      },
    });

    volumeSeries.setData([...volumeData]);
    if (logsData) {
      let markedData: any[] = [];

      logsData.forEach((each, index) => {
        const baseColor = each?.result >= 0 ? "37, 146, 133" : "239, 68, 68";

        if (gridData ? index > logsData.length - 120 : index < 120) {
          const lineSeries = chart.addSeries(AreaSeries, {
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: false,
            lineColor: `rgba(${baseColor}, 1)`,
            topColor: `rgba(${baseColor}, 0.6)`,
            bottomColor: `rgba(${baseColor}, 0.05)`,
          });
          if (!each.trade_taken_at) return;
          if (each.order_status === "closed") {
            try {
              lineSeries.setData([
                {
                  time: Number(each.trade_taken_at) as Time,
                  value: each.open_price ?? Number(each?.order_price),
                },
                {
                  time: Number(each.trade_closed_at) as Time,
                  value: each.close_price ?? Number(each?.close_value),
                },
              ]);
            } catch (error) {
              console.warn("error adding line series");
            }
          } else if (each.order_status === "pending") {
            try {
              lineSeries?.setData([
                { time: Number(each.trade_taken_at), value: each.open_price },
                {
                  time: Number(graphData?.[graphData.length - 1].time),
                  value: each.open_price,
                },
              ]);
            } catch (error) {
              console.warn("error adding line series");
            }
          }
        } else {
          const point1 = {
            time: Number(each.trade_taken_at) as Time,
            price: each.open_price ?? Number(each?.order_price),
          };
          const point2 = {
            time: Number(each.trade_closed_at) as Time,
            price: each.close_price ?? Number(each?.close_value),
          };
          const trend = new TrendLine(chart, lineSeries, point1, point2, {
            lineColor: `rgba(${baseColor}, 1)` || "red",
            width: 1.5,
            showLabels: false,
            labelBackgroundColor: "transparent",
            labelTextColor: "white",
          });
          lineSeries.attachPrimitive(trend);
        }

        const baseMarker = {
          time: Number(each.trade_taken_at),
          text: "O",
          position: each.decision === "Sell" ? "aboveBar" : "belowBar",
          color: `rgba(${baseColor}, 1)`,
          shape: each.decision === "Sell" ? "arrowDown" : "arrowUp",
        };
        markedData.push(baseMarker);

        if (each.order_status === "closed") {
          const closedMarker = {
            time: Number(each.trade_closed_at),
            text: "C",
            position: "aboveBar",
            color: `rgba(${baseColor}, 1)`,
            shape: "arrowDown",
            takenAt: each.trade_taken_at,
          };
          markedData.push(closedMarker);
        }
      });

      markedData.sort(
        (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
      );

      if (markedData.length) {
        createSeriesMarkers(lineSeries, markedData);
      }
    }
    createTextWatermark(chart.panes()[0], {
      horzAlign: "center",
      vertAlign: "center",
      lines: [
        {
          fontSize: 50,
          color: "rgba(229, 235, 241, 0.1 )",
          text: graphDataOtherDetails.symbol
            ? `${graphDataOtherDetails.symbol}, ${graphDataOtherDetails.timestamp}`
            : "No Data",
        },
      ],
    });

    chartRef.current = chart;

    const visibleDataPoints = 100;
    if (graphData && graphData.length > visibleDataPoints) {
      const firstPoint = graphData[graphData.length - visibleDataPoints];
      const lastPoint = graphData[graphData.length - 1];
      chart.timeScale().setVisibleRange({
        from: firstPoint.time,
        to: lastPoint.time,
      });
    }

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0].contentRect) {
        const { width, height } = entries[0].contentRect;
        chart.resize(width, height);
      }
    });

    resizeObserver.observe(chartContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
    };
  }, [
    chartContainerRef,
    loading,
    graphData,
    volumeData,
    logsData,
    graphDataOtherDetails,
    canvasData,
    enableIndicator,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const onChange = () => {
      setTimeout(() => setChartLoaded(true), 500);

      chart.timeScale().unsubscribeVisibleTimeRangeChange(onChange);
    };

    chart.timeScale().subscribeVisibleTimeRangeChange(onChange);

    return () => chart.timeScale().unsubscribeVisibleTimeRangeChange(onChange);
  }, []);

  useEffect(() => {
    if (chartLoaded && !gridData)
      if (enableIndicator && canvasData && !indicatorsLoaded.current) {
        const indicatorsData = JSON.parse(canvasData);
        indicatorsData?.forEach(async (element: any) => {
          if (
            element.type === "selectorNode" &&
            (element?.data?.type === "Indicator" ||
              element?.data?.title == "Grid")
          ) {
            const lineSeries = await addIndicatorToChart({
              indicator: {
                ...element.data,
                short_name:
                  element?.data?.title === "Grid"
                    ? "grid"
                    : element.data.short_name,
              },
              chartRef,
              tickerData: graphData,
              indicatorData: [],
              lineSeriesRef: [],
              gridData: gridData,
              candleStickSeriesRef: candleStickSeriesRef,
              currentPrice: currentPrice,
              postCommonIndicators: postCommonIndicators,
            });
            indicatorsLoaded.current = true;
          }
        });
      }
  }, [chartLoaded, gridData, enableIndicator]);

  useEffect(() => {
    if (chartLoaded && gridData && !isEmpty(gridData))
      if (enableIndicator && canvasData) {
        if (invertedChart) {
          chartRef.current.timeScale().setVisibleRange({
            from: graphData[0]?.time,
            to: graphData[100]?.time,
          }); // if it's a grid startegy set visible range to 100 points by default
        }
        addGrid(
          [],
          { short_name: "grid" },
          chartRef,
          undefined,
          candleStickSeriesRef,
          gridData?.buy_levels,
          gridData?.sell_levels,
          gridData?.base_price
        );
      }
  }, [chartLoaded, gridData, enableIndicator]);

  return (
    <div
      ref={chartContainerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    />
  );
};

export default memo(LightWeightChart);
